import { useGetFrontRichiesteIdModuliDeleghe } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type RichiestaModuloDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTO.ts';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { SectionTitle } from '@framework/ui/components/summary/SectionTitle.tsx';
import { SummaryDetailCard } from '@framework/ui/components/summary/SummaryDetailCard.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TbFiles } from 'react-icons/tb';
import { DelegationLogic } from '../../../module/delegation/DelegationLogic.ts';
import { useSummaryAdditionalData } from '../SummaryAdditionalDataContext.tsx';
import {
  getModuleRow,
  getModulesColumns,
  type IModuleTableRow,
} from './modulesColumns.tsx';

interface IExtendedModulesTableProps {
  submissionId: string;
  modules: RichiestaModuloDTO[];
  submission?: RichiestaDTO;
}

/**
 * Tabella moduli nel riepilogo esteso.
 * Mostra i moduli in formato tabella con ricerca locale per colonna.
 * Per ogni modulo con versione omissis, aggiunge una riga aggiuntiva.
 */
export function ExtendedModulesTable(props: IExtendedModulesTableProps) {
  const { submissionId, modules, submission } = props;
  const { t } = useTranslation();

  const { data: delegheData } =
    useGetFrontRichiesteIdModuliDeleghe(submissionId);

  const additionalData = useSummaryAdditionalData();

  const moduleDelegations = useMemo(
    () =>
      DelegationLogic.getModuleDelegations({
        modules,
        data: delegheData ?? [],
      }),
    [modules, delegheData],
  );

  const dataSource = useMemo(() => {
    const rows: IModuleTableRow[] = [];

    for (const module of modules) {
      const delegation = moduleDelegations.find(
        d => d.submissionModule.id === module.id,
      );
      // L'abbinamento è per `moduli[].id`, come previsto dalla specifica
      const usecase = module.id
        ? additionalData?.modulesById.get(module.id)?.denominazioneFattispecie
        : undefined;
      rows.push(getModuleRow(module, false, delegation, usecase ?? undefined));

      if (module.idFileOmissis != null) {
        rows.push(getModuleRow(module, true, delegation, usecase ?? undefined));
      }
    }

    return rows;
  }, [modules, moduleDelegations, additionalData]);

  const showUsecase = useMemo(
    () => dataSource.some(row => row.usecase !== ''),
    [dataSource],
  );

  const columns = useMemo(
    () => getModulesColumns(submissionId, submission, showUsecase),
    [submission, submissionId, showUsecase],
  );

  return (
    <div>
      <SectionTitle
        icon={<TbFiles />}
        title={t('submission-summary.elenco-moduli', 'Elenco dei Moduli')}
      />
      <VerticalSpace />
      <SummaryDetailCard noPadding>
        <TableData
          removeShadow
          dataSource={dataSource}
          columns={columns}
          rowKey="key"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20],
            hideOnSinglePage: true,
          }}
          size="middle"
        />
      </SummaryDetailCard>
    </div>
  );
}
